import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

export interface SiteSettings {
  primaryColor: string;
  companyName: string;
  companyEmail: string;
  companyPhone: string;
  companyAddress: string;
  companyTagline: string;
  siteUrl: string;
}

const DEFAULT_SETTINGS: SiteSettings = {
  primaryColor: '#0d9488',
  companyName: 'SwiftTrack',
  companyEmail: 'support@swift-tracks.com',
  companyPhone: '+1 (800) 794-8725',
  companyAddress: '1200 Logistics Blvd, Dallas, TX 75201, USA',
  companyTagline: 'Swift. Reliable. Delivered.',
  siteUrl: 'https://swift-tracks.com',
};

function applyColorVars(primaryColor: string) {
  document.documentElement.style.setProperty('--primary', primaryColor);
  document.documentElement.style.setProperty(
    '--primary-dark',
    `color-mix(in srgb, ${primaryColor} 75%, black)`
  );
  document.documentElement.style.setProperty(
    '--primary-light',
    `color-mix(in srgb, ${primaryColor} 15%, white)`
  );
}

interface SiteSettingsRow {
  id: number;
  primary_color: string;
  company_name: string;
  company_email: string;
  company_phone: string;
  company_address: string;
  company_tagline: string;
  site_url: string;
}

function rowToSettings(row: SiteSettingsRow | null | undefined): SiteSettings {
  if (!row) return DEFAULT_SETTINGS;
  return {
    primaryColor: row.primary_color || DEFAULT_SETTINGS.primaryColor,
    companyName: row.company_name || DEFAULT_SETTINGS.companyName,
    companyEmail: row.company_email || DEFAULT_SETTINGS.companyEmail,
    companyPhone: row.company_phone || DEFAULT_SETTINGS.companyPhone,
    companyAddress: row.company_address || DEFAULT_SETTINGS.companyAddress,
    companyTagline: row.company_tagline || DEFAULT_SETTINGS.companyTagline,
    siteUrl: row.site_url || DEFAULT_SETTINGS.siteUrl,
  };
}

interface SiteSettingsContextType {
  settings: SiteSettings;
  updateSettings: (updated: Partial<SiteSettings>) => void;
  saveSettings: (updated: SiteSettings) => void;
}

const SiteSettingsContext = createContext<SiteSettingsContextType>({
  settings: DEFAULT_SETTINGS,
  updateSettings: () => {},
  saveSettings: () => {},
});

export function SiteSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(DEFAULT_SETTINGS);

  // Load the single settings row from Supabase on mount
  useEffect(() => {
    let cancelled = false;
    supabase
      .from('site_settings')
      .select('*')
      .eq('id', 1)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const next = rowToSettings(data as SiteSettingsRow | null);
        setSettings(next);
        applyColorVars(next.primaryColor);
      })
      .catch(() => {
        // fall back to defaults on any load error
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    applyColorVars(settings.primaryColor);
  }, [settings.primaryColor]);

  const updateSettings = (updated: Partial<SiteSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...updated };
      if (updated.primaryColor) applyColorVars(updated.primaryColor);
      return next;
    });
  };

  const saveSettings = (updated: SiteSettings) => {
    setSettings(updated);
    applyColorVars(updated.primaryColor);

    supabase
      .from('site_settings')
      .upsert(
        {
          id: 1,
          primary_color: updated.primaryColor,
          company_name: updated.companyName,
          company_email: updated.companyEmail,
          company_phone: updated.companyPhone,
          company_address: updated.companyAddress,
          company_tagline: updated.companyTagline,
          site_url: updated.siteUrl,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'id' }
      )
      .then(({ error }) => {
        if (error) console.error('Failed to save site settings:', error.message);
      });
  };

  return (
    <SiteSettingsContext.Provider value={{ settings, updateSettings, saveSettings }}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}